/* Studio Artemist - fit the box to the ARTWORK, not to the file.
   2026-09-16.

   WHY
   Every master in this store is a square file, but the artwork inside it is
   not square and its shape varies per product. Measured on one collection:
     Floral Bouquet    489x489 file, artwork 0.77 upright, 16%/18% white sides
     Utopian Jungle 2  489x489 file, artwork 0.72 upright, 20%/19% white sides
     A New Dawn        489x489 file, artwork 1.91 WIDE,    27% white top, 25% bottom
   One fixed box shape therefore cannot fit all three. A square box leaves a
   quarter of the panorama card blank; a tall box crops the uprights.

   WHAT THIS DOES
   Samples each image on a 64px canvas, finds the bounding box of the real
   artwork by trimming near-white and transparent padding, then classifies:

     ARTWORK-ON-WHITE (padding > 8% on an axis)
       -> sets --sa-art-ar on the container so the BOX takes the artwork's
          shape. object-fit: cover then trims only the white padding, so the
          artwork fills the box exactly: nothing cropped, nothing blank.

     ROOM PHOTO (little or no padding - a lifestyle shot of art on a wall)
       -> leaves the designed box alone and sets --sa-art-pos so the fill is
          biased towards the wall art, which sits in the upper half. The box
          stays full, so banners keep reading as banners.

   Results are cached in sessionStorage, so each image is measured once.
   A cross-origin image taints the canvas; that is caught and the element is
   left exactly as the theme styled it.

   TO REVERT: remove the sa-artwork-fit.js line from snippets/stylesheets.liquid.
*/
(function () {
  'use strict';

  var SEL = [
    '.sa-collection-story__feature',
    '.sa-collection-advisory__media',
    '.sa-collection-story__hero-media',
    '.sa-lgh__mood',
    '.sa-lgh__hero-room',
    '.sa-lgh__making-image',
    '.sa-lgh__work-image',
    '.sa-lgh__story-image',
    '[data-sa-art-fit]'
  ].join(',');

  var CACHE_KEY = 'sa-art-fit-v1';
  var PAD_THRESHOLD = 0.08; // 8% white on an axis means the artwork is inset
  var AR_MIN = 0.45, AR_MAX = 2.8;

  var cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { cache = {}; }
  var dirty = false;

  function persist() {
    if (!dirty) return;
    dirty = false;
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* private mode */ }
  }

  /* Returns {ar, inset} for an image, or null when it cannot be read. */
  function measure(img) {
    var key = (img.currentSrc || img.src || '').split('?')[0];
    if (!key) return null;
    if (cache[key]) return cache[key];
    if (!img.naturalWidth || !img.naturalHeight) return null;

    var W = 64;
    var H = Math.max(1, Math.round(W * img.naturalHeight / img.naturalWidth));
    var canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;

    var data;
    try {
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, W, H);
      data = ctx.getImageData(0, 0, W, H).data;
    } catch (e) {
      return null; // tainted canvas - leave the theme's own styling in place
    }

    var top = H, bottom = -1, left = W, right = -1;
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        var i = (y * W + x) * 4;
        if (data[i + 3] < 12) continue;
        if (data[i] > 242 && data[i + 1] > 242 && data[i + 2] > 242) continue;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
    if (bottom < 0) return null; // nothing but background

    var aw = right - left + 1, ah = bottom - top + 1;
    var ar = aw / ah;
    if (!isFinite(ar) || ar <= 0) return null;

    var padX = (W - aw) / W, padY = (H - ah) / H;
    var result = {
      ar: Math.round(Math.min(AR_MAX, Math.max(AR_MIN, ar)) * 1000) / 1000,
      inset: (padX > PAD_THRESHOLD || padY > PAD_THRESHOLD) ? 1 : 0
    };
    cache[key] = result;
    dirty = true;
    return result;
  }

  function apply(el) {
    if (el.hasAttribute('data-sa-art-fitted')) return;
    var img = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (!img) return;

    if (!img.complete || !img.naturalWidth) {
      // These few images decide their own box size, so a lazy one would let
      // the card render at the theme default and then visibly resize when the
      // shopper scrolls to it. Pull them forward - there are under a dozen per
      // page - at low priority so they do not compete with the hero.
      if (img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
        try { img.fetchPriority = 'low'; } catch (e) { /* older browsers */ }
      }
      img.addEventListener('load', function () { apply(el); persist(); }, { once: true });
      return;
    }

    var m = measure(img);
    if (!m) return;

    if (m.inset) {
      // Artwork sits inside white space: give the box the artwork's own shape.
      el.style.setProperty('--sa-art-ar', String(m.ar));
      el.setAttribute('data-sa-art-fit-mode', 'artwork');
    } else {
      // Lifestyle/room photo: keep the designed box, bias the fill upwards so
      // the art on the wall stays in frame while the box remains full.
      el.setAttribute('data-sa-art-fit-mode', 'room');
    }
    el.setAttribute('data-sa-art-fitted', '');
  }

  function run() {
    var els = document.querySelectorAll(SEL);
    for (var i = 0; i < els.length; i++) apply(els[i]);
    persist();
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || setTimeout)(function () { queued = false; run(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);
  document.addEventListener('shopify:section:load', schedule);

  // Collection filtering and pagination replace the grid, so watch for new cards.
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
